import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import { appraisalStageIndex, appraisalStages, formatDate } from "../../utils/appraisal";

/** The five appraisal stages as a vertical timeline: who does what, and when.
 *  Done stages are ticked, the current one is highlighted. */
export default function AppraisalJourney({
  status,
  managerName,
  dates,
  compact = false,
}: {
  status: string;
  managerName?: string | null;
  /** Due dates keyed by stage. */
  dates?: Partial<Record<"self" | "peers" | "manager" | "meeting", string | null>>;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const stages = appraisalStages(managerName);
  const current = appraisalStageIndex(status);
  const finished = status === "completed";

  return (
    <ol className="relative">
      {stages.map((stage, i) => {
        const done = finished || i < current;
        const now = !finished && i === current;
        const date = dates?.[stage.key as keyof NonNullable<typeof dates>];
        return (
          <li key={stage.key} className="relative flex gap-3 pb-4 last:pb-0">
            {i < stages.length - 1 && (
              <span
                aria-hidden
                className={`absolute left-[13px] top-7 h-[calc(100%-1.5rem)] w-0.5 ${done ? "bg-pme-blue" : "bg-gray-200"}`}
              />
            )}
            <span
              className={[
                "relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                done
                  ? "bg-pme-blue text-white"
                  : now
                    ? "bg-white text-pme-blue ring-2 ring-pme-blue"
                    : "bg-gray-100 text-gray-400",
              ].join(" ")}
            >
              {done ? <Check size={14} strokeWidth={2.75} aria-hidden /> : i + 1}
            </span>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                <p className={`text-sm font-semibold ${now ? "text-pme-navy" : done ? "text-gray-700" : "text-gray-500"}`}>
                  {stage.title}
                  {now && (
                    <span className="ml-2 rounded-full bg-pme-blue/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-pme-blue">
                      {t("appraisal.nowBadge", "Now")}
                    </span>
                  )}
                </p>
                {date && !done && (
                  <span className={`text-xs ${now ? "font-semibold text-pme-navy" : "text-gray-400"}`}>
                    {formatDate(date)}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">{stage.who}</p>
              {!compact && (now || !done) && (
                <p className={`mt-0.5 text-xs ${now ? "text-gray-600" : "text-gray-400"}`}>{stage.detail}</p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
